import { forwardRef, useState, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { useEmbedTokens } from "../theme";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: "default" | "danger";
}

// Inline text link for ACTIONS (the embed's "Clear filters"/"+ Add new address"/"Remove").
// Padding 0, no height, no border — the shape EmbedButton's fixed geometry can't express.
// TD-329: button-only by design (all call sites are actions, none navigate);
// the spec's optional href/<a> path is deferred as YAGNI until a nav site appears.
export const EmbedLink = forwardRef<HTMLButtonElement, Props>(function EmbedLink(
  { variant = "default", onMouseEnter, onMouseLeave, children, ...rest }: Props,
  ref,
) {
  const t = useEmbedTokens();
  const [hovered, setHovered] = useState(false);
  const color = variant === "danger" ? t.colors.error : t.colors.primary;
  const style: CSSProperties = {
    fontFamily: "inherit", fontSize: t.fontSize.sm, fontWeight: 600, color,
    // border: 0 (not "none") — React 19 silently drops `border: "none"` from inline
    // styles, which would leave the <button>'s UA `2px outset buttonface` visible.
    background: "transparent", border: 0, padding: 0, margin: 0,
    cursor: "pointer", display: "inline",
    textDecoration: hovered ? "underline" : "none",
  };
  return (
    <button ref={ref} type="button" style={style}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e); }}
      {...rest}>
      {children}
    </button>
  );
});
